import { locHrefUtil, apiConfigHelper, MANAGER_VIEW_APP_PREFIX, getGlobalFeatureToggle } from '@makemydeal/dr-shared-ui-utils';
import { RSAA } from 'redux-api-middleware';
import {
    SCENARIOS_FETCH_FAILURE,
    SCENARIOS_FETCH_REQUEST,
    SCENARIOS_FETCH_SUCCESS,
    SCENARIOS_INGEST_COMPLETE,
    OUT_OF_BAND_BANNER_DISPLAYED,
    OUT_OF_BAND_BANNER_HIDDEN,
    TEMPLATE_SCENARIO_FETCH_REQUEST,
    TEMPLATE_SCENARIO_FETCH_SUCCESS,
    TEMPLATE_SCENARIO_FETCH_FAILURE,
    UPDATE_ACTIVE_COMPARISON,
    COPY_DEAL_SCENARIO_CLICKED,
    DELETE_DEAL_SCENARIO_CLICKED,
    SELECT_PRIMARY_DEAL_SCENARIO_CLICKED
} from '../actionTypes/multiScenarioActionTypes';
import { MAKE_CURRENT_COMPLETED } from '../actionTypes/offerActionTypes';
import { RESTORED_ACTIVE_CELL_META } from '../constants';
import type { GenericObject, OfferType } from '@makemydeal/dr-platform-types';
import { LEASE, PaymentScheduleTypes, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import { setLockPaymentInputsInSV } from './dealLockingActionCreators';

import {
    constants as offerReduxConstants,
    padPaymentGridAxis,
    PaymentGridSource,
    paymentPayloadUtils,
    utils as offerReduxUtils
} from '@makemydeal/dr-offer-redux';
import type { PaymentGridRestoredInputs } from '@makemydeal/dr-offer-redux';
import { dealerSelectors, featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors, scopedActionsCreators, scopedActions } from '@makemydeal/shared-scoped-store';

import * as offerReduxSelectors from '../selectors/offerRedux';
import * as offerReduxActionCreators from '../actions/offerRedux';
import * as multiScenarioSelectors from '../selectors/multiScenarioSelectors';
import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';
import * as unsavedChangesSelectors from '../selectors/unsavedChangesSelectors';
import { getOrderForTemplateScenarios, getOrderFromRetrievedScenarios } from '../utils/multiScenarioUtils';
import { buildTermId, restoreFlexBuyGridFromStored } from '../utils/paymentGridBaggageUtils';
import { decompressPayload } from '@makemydeal/dr-common-utils';
import {
    getPaymentGridFreshness,
    getPaymentGridScenarioEntry,
    resolvePaymentGridForScenario,
    type PaymentGrid
} from '@makemydeal/dr-platform-shared';
import type { ScenarioReference } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

export const fetchScenarios = (
    dealXgId: string,
    scenarioIds: string[],
    params?: { [key: string]: string | boolean },
    dealXgVersion?: string
) => {
    const scenarioIdQuery = scenarioIds.join(',');
    const locHref = locHrefUtil.getLocHref();
    let relativePath = `scenarios/${dealXgId}?scenarioIds=${scenarioIdQuery}`;

    if (params) {
        relativePath = `${relativePath}&${Object.entries(params)
            .map(([key, value]) => `${key}=${value}`)
            .join('&')}`;
    }

    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, relativePath);
    const endpoint = bffApiUrlResult.url;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [
                SCENARIOS_FETCH_REQUEST,
                { type: SCENARIOS_FETCH_SUCCESS, ...(dealXgVersion && { meta: { dealXgVersion } }) },
                SCENARIOS_FETCH_FAILURE
            ]
        }
    };
};

export const fetchTemplateScenario = (dealXgId: string, scenarioId: string, templateId: string) => {
    const locHref = locHrefUtil.getLocHref();
    const relativePath = `deals/${dealXgId}/scenarios/${scenarioId}/templates/${templateId}`;

    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, relativePath);
    const endpoint = bffApiUrlResult.url;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [
                { type: TEMPLATE_SCENARIO_FETCH_REQUEST, meta: { scenarioId } },
                TEMPLATE_SCENARIO_FETCH_SUCCESS,
                TEMPLATE_SCENARIO_FETCH_FAILURE
            ]
        }
    };
};

/**
 * Thunk action creator that dispatches actions for loading additional scenarios
 */
export const handleFetchMultipleScenarios =
    () =>
    (dispatch: any, getState: any): void => {
        const state = getState();

        const activeComparison = multiScenarioSelectors.getActiveComparison(state);
        const appliedTemplateId = dealTemplateSelectors.getAppliedDealTemplateId(state);
        const isTemplateManuallyApplied = dealTemplateSelectors.isDealTemplateManuallyApplied(state);

        const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
        const primaryScenario = activeComparison?.scenarios.find(({ isPrimary }) => isPrimary);

        if (activeComparison) {
            if (activeComparison.isPublished || isTemplateManuallyApplied) {
                if (primaryScenario) {
                    dispatch(handleLoadScenarioPaymentGrid(primaryScopeId, primaryScenario));
                    if (primaryScenario.isLocked !== undefined) {
                        const currentOfferType = state.offer.offerType;
                        dispatch(setLockPaymentInputsInSV(currentOfferType, primaryScenario.isLocked));
                    }
                }
            }

            if (activeComparison.scenarios.length > 1 && !getGlobalFeatureToggle('enableMVMSLoadAsync')) {
                const dealXgId = offerReduxSelectors.getDealXgId(state);
                const nonPrimaryScenarioIds = activeComparison.scenarios.filter(({ isPrimary }) => !isPrimary).map(({ id }) => id);

                /**
                 * NOTE: deal templates has legacy flow
                 * when acceleron /init returns fake baggage (with 3 scenarios with the primary deal version) and
                 * when acceleron /getScenarios receives param templateId it gets scenarios as deals, not as scenarios.
                 * For manual applying we'd like to use standard flow for /getScenarios with getting scenarios
                 */
                const templateIdParam =
                    appliedTemplateId && !isTemplateManuallyApplied ? { templateId: appliedTemplateId } : undefined;

                dispatch(fetchScenarios(dealXgId, nonPrimaryScenarioIds, templateIdParam));
            }
        }

        // A saved `startingCash` always wins, draft comparisons included — a draft save writes the field the
        // user typed just as a publish does. Not gated on `isPublished` like the payment grid above:
        // resolveStartingCash falls through to the equity allocation split only when the scenario has none.
        dispatch(handleLoadScenarioStartingCash(primaryScopeId, primaryScenario));
    };

/**
 * Feature toggles are session/dealer-global, not per-scenario. A saved scenario carries a persisted
 * `featureToggles` snapshot in its `INIT_PENCIL_SUCCESS` payload; because the GLOBAL featureToggleReducer
 * merges every `INIT_PENCIL_SUCCESS` (scoped or not), hydrating a non-primary scenario would clobber the
 * live global toggles with that stale snapshot — DE749297: e.g. `enableAiAltDealStructureRecommendations`
 * flips true -> false on multi-scenario load, hiding the AI "Alternative Deal Structures" card.
 *
 * Strip `featureToggles` from non-primary scenario hydration payloads so only the primary/bootstrap
 * `INIT_PENCIL_SUCCESS` owns the global toggle slice. A scope's offer slice gets the primary's live toggles
 * from `withPrimaryOfferToggles` instead.
 */
export const withoutScenarioFeatureToggles = (scenario: any): any => {
    if (scenario && typeof scenario === 'object' && 'featureToggles' in scenario) {
        const rest = { ...scenario };
        delete rest.featureToggles;
        return rest;
    }
    return scenario;
};

/**
 * Adds the live toggles to a stripped hydration payload. Under DE774740 that is the primary scope's whole offer toggle
 * set, under a key only offer-redux reads, so the scope's offer slice matches the primary's without the global slice
 * being written. Otherwise it is the live F298789_ASP_PG_redesign value alone, which only the load mapper reads.
 */
const withLiveRedesignToggle = (payload: any, liveState: any): any => {
    if (!featureToggleSelectors.isAspPaymentGridRedesignEnabled(liveState)) return payload;
    return { ...payload, scenarioFeatureToggles: { [offerReduxConstants.ASP_PG_REDESIGN_TOGGLE]: true } };
};

export const withPrimaryOfferToggles = (payload: any, liveState: any): any => {
    if (!payload || typeof payload !== 'object') return payload;
    if (!featureToggleSelectors.isMultiScenarioGridConsistencyEnabled(liveState)) return withLiveRedesignToggle(payload, liveState);
    const primaryScopeId = liveState?.__scopeMap ? scopedStateSelectors.getPrimaryScopeId(liveState) : undefined;
    const primaryState = (primaryScopeId && scopedStateSelectors.getScopeStateById(liveState, primaryScopeId)) || liveState;
    const primaryOfferToggles = primaryState?.offer?.featureToggles;
    if (!primaryOfferToggles || !Object.keys(primaryOfferToggles).length) return withLiveRedesignToggle(payload, liveState);
    return { ...payload, primaryOfferFeatureToggles: { ...primaryOfferToggles } };
};

/**
 * Accepts full alternate scenarios retrieved from DXG and adds them to the scope state, then initializes the scopes
 * @param nonPrimaryScenarios
 * @returns
 */
export const addAlternateScenariosToState =
    (nonPrimaryScenarios: any[]) =>
    (dispatch: any, getState: any): void => {
        const state = getState();
        const activeComparison = multiScenarioSelectors.getActiveComparison(state);

        if (activeComparison && nonPrimaryScenarios.length) {
            const retrievedNonPrimaryScenarioIds = nonPrimaryScenarios.map(({ offer }: any) => offer.dealExchangeVersionId);
            const baggageScenariosById = new Map(activeComparison.scenarios.map((scenario) => [scenario.id, scenario]));

            // Hydrate each scenario
            for (const scenario of nonPrimaryScenarios) {
                const scenarioId = scenario.offer.dealExchangeVersionId;
                const newAction = scopedActions.createScopedAction(scenarioId, {
                    type: initActionTypes.INIT_PENCIL_SUCCESS,
                    payload: withPrimaryOfferToggles(withoutScenarioFeatureToggles(scenario), getState())
                });
                dispatch(newAction);
            }

            dispatch(scopedActionsCreators.initializeScopes(retrievedNonPrimaryScenarioIds));

            // Hydrate PaymentGrid for each scenario after initializing them
            for (const scenario of nonPrimaryScenarios) {
                const scenarioId = scenario.offer.dealExchangeVersionId;
                const baggageScenario = baggageScenariosById.get(scenarioId);
                dispatch(handleLoadScenarioPaymentGrid(scenarioId, baggageScenario!));

                if (baggageScenario?.isLocked !== undefined) {
                    const lockAction = setLockPaymentInputsInSV(scenario.offer.type, baggageScenario.isLocked);
                    const scopedLockAction = scopedActions.createScopedAction(scenarioId, lockAction);
                    dispatch(scopedLockAction);
                }

                // Manually applied templates also route through here; handleLoadScenarioStartingCash skips those scopes.
                dispatch(handleLoadScenarioStartingCash(scenarioId, baggageScenario));
            }
            dispatch({ type: SCENARIOS_INGEST_COMPLETE });
        }
    };

/**
 * Accepts full template scenarios retrieved from template service and adds them to the scope state, then initializes the scopes
 * @param nonPrimaryScenarios
 * @returns
 */
export const addTemplateScenariosToState =
    (nonPrimaryScenarios: any[]) =>
    (dispatch: any, getState: any): void => {
        const state = getState();
        const activeComparison = multiScenarioSelectors.getActiveComparison(state);
        if (activeComparison && nonPrimaryScenarios.length) {
            const order = scopedStateSelectors.getScopesOrder(state);
            const [_, ...generatedIds] = order;

            // Hydrate each scenario
            nonPrimaryScenarios.forEach((scenario, index) => {
                const newAction = scopedActions.createScopedAction(generatedIds[index], {
                    type: initActionTypes.INIT_PENCIL_SUCCESS,
                    payload: withPrimaryOfferToggles(withoutScenarioFeatureToggles(scenario), getState())
                });
                dispatch(newAction);
            });

            dispatch(scopedActionsCreators.initializeScopes(order));

            // Hydrate PaymentGrid for each scenario after initializing them
            nonPrimaryScenarios.forEach((_, index) => {
                const scenarioId = generatedIds[index];
                // The first scenario is primary, non primary starts from index 1
                const baggageScenario = activeComparison.scenarios[index + 1];
                if (baggageScenario) {
                    dispatch(handleLoadScenarioPaymentGrid(scenarioId, baggageScenario));
                }
            });
            // No Cash Received seed for template scopes (US2067616) — see handleLoadScenarioStartingCash.
            dispatch({ type: SCENARIOS_INGEST_COMPLETE });
        }
    };

const getSavedSellRateUnit = (mfOverride?: number, aprOverride?: number): 'MoneyFactor' | 'APR' | undefined => {
    if (mfOverride != null) {
        if (mfOverride === 0) return undefined;
        if (offerReduxUtils.exceedsMoneyFactorBound(mfOverride)) return 'APR';
        return 'MoneyFactor';
    }
    if (aprOverride != null) return 'APR';
    return undefined;
};

/**
 * Pure function that builds the array of payment grid actions for a scenario.
 * Used by both the thunk `handleLoadScenarioPaymentGrid` and the bulk hydration path.
 */
export const buildPaymentGridActions = (
    baggageScenario: Record<string, any>,
    offerType: OfferType,
    isTemplateApplied: boolean,
    isRedesignEnabled = false,
    isExplicitLenderGridActive = false
): any[] => {
    // Can't use optional chaining due to transpilation for Jest
    const paymentGrid = baggageScenario && baggageScenario.paymentGrid;
    if (!paymentGrid || !offerType) {
        return [];
    }

    const actions: any[] = [];

    /**
     * Axes and cells are read defensively throughout, because this grid can arrive decompressed out of a
     * deal saved by an older build rather than only as a well-formed object built in memory. A missing
     * `rows`, `columns` or `cells` used to throw here and take the whole scenario ingest down — and a stale
     * grid now reaches this builder by design, so the ragged shapes are no longer hypothetical.
     */
    /**
     * Not filtered, only guarded: `selectedTermIndex` and `selectedDownPaymentIndex` below come from the
     * unfiltered `cells`, and are used to index these axes — dropping a null entry would shift them and
     * point the deal at a different term or cash down. Each entry is read with `?.` instead.
     */
    const gridRows: any[] = Array.isArray(paymentGrid.rows) ? paymentGrid.rows : [];
    const gridColumns: any[] = Array.isArray(paymentGrid.columns) ? paymentGrid.columns : [];
    if (!gridRows.length || !gridColumns.length) {
        return [];
    }

    // Initialize axes
    const cashDown = padPaymentGridAxis(gridRows.map((row: any) => row?.dueAtSigning));
    const terms = padPaymentGridAxis(gridColumns.map((column: any) => column?.term));
    actions.push(offerReduxActionCreators.initializePaymentGridTerms(offerType, terms));
    actions.push(offerReduxActionCreators.initializePaymentGridCashdown(offerType, cashDown));

    // Initialize overrides
    const lenderPrograms: GenericObject[] = gridColumns.filter((column: any) => column?.lenderProgram);
    const hasLenderPrograms = Boolean(lenderPrograms.length);
    const paymentGridCells: any[] = Array.isArray(paymentGrid.cells) ? paymentGrid.cells : [];
    /**
     * US2085313 / DEP prerequisite: a grid with no selected cell is legitimate, and must not throw.
     *
     * `.pop()!` returned undefined for such a grid and destructuring it threw a TypeError, taking the
     * whole hydration down. A re-priced grid genuinely has no selection — it was priced against the deal
     * rather than the deal being moved to it — so the indices stay undefined and nothing downstream
     * claims a selected term or a current cash down.
     */
    const [selectedTermIndex, selectedDownPaymentIndex] = paymentGridCells
        .flatMap((row: any[], downPaymentIndex: number) =>
            // Row and cell are read defensively: this grid can arrive decompressed out of the deal, where a
            // ragged shape is possible, rather than only as a well-formed object built in memory.
            (row ?? []).flatMap((cell, termIndex: number) => (cell?.isSelected ? [[termIndex, downPaymentIndex]] : []))
        )
        .pop() ?? [undefined, undefined];

    const savedColumn = paymentGrid.selectedColumnIndex ?? selectedTermIndex;
    const savedColumnTerm = savedColumn == null ? undefined : gridColumns[savedColumn]?.term;
    if (isRedesignEnabled) {
        const savedRow = paymentGrid.selectedRowIndex ?? selectedDownPaymentIndex;
        if (savedColumn != null) {
            actions.push({
                ...offerReduxActionCreators.setPaymentGridSelectedColumn(offerType, savedColumn),
                meta: { [RESTORED_ACTIVE_CELL_META]: true }
            });
        }
        if (savedRow != null) actions.push(offerReduxActionCreators.setPaymentGridSelectedRow(offerType, savedRow));
    }

    const ownCellsByColumn: Record<number, Record<number, any>> = {};
    const restoredInputs: Record<number, PaymentGridRestoredInputs> = {};

    gridColumns.forEach((column: any, index: number) => {
        const {
            term,
            lenderProgram,
            mfOverride,
            aprOverride,
            residualPercentOverride,
            residualDollarOverride,
            source,
            sellRateOverridden,
            residualOverridden
        } = column ?? {};
        const sellRate = mfOverride ?? aprOverride;
        const rateType = isRedesignEnabled && offerType === LEASE ? getSavedSellRateUnit(mfOverride, aprOverride) : undefined;
        const isProgramBackedColumn = isExplicitLenderGridActive && source === PaymentGridSource.ProgramQuotes;
        const isTermSlotHeldBySelectedColumn =
            isRedesignEnabled && index !== savedColumn && term != null && term === savedColumnTerm;
        if (sellRate != null && !isTermSlotHeldBySelectedColumn) {
            actions.push(
                offerReduxActionCreators.updateSellRateOverride(
                    sellRate,
                    term,
                    {
                        isSelectedTerm: index === selectedTermIndex,
                        skipPaymentCall: true,
                        paymentGridLenderProgram: lenderProgram,
                        rateType
                    },
                    offerType
                )
            );
        }
        // The saved key names the unit the residual was entered in. Deriving it from the offer type
        // instead would restore a dollar-typed lease residual as a percent — $18,000 as an 18000%
        // residual — since a lease term can be overridden in dollars from the deal screen's $/% toggle.
        const isDollarResidual = residualDollarOverride != null;
        const residualOverride = isDollarResidual ? residualDollarOverride : residualPercentOverride;

        const ownResidualInfo =
            residualOverride == null
                ? undefined
                : {
                      baseResidualType: isDollarResidual ? RESIDUAL_DOLLAR : RESIDUAL_PERCENT,
                      [isDollarResidual ? 'programResidual' : 'programResidualPercent']: residualOverride,
                      // Both halves, the shape a column record has to carry (US2081458): the column supplies only
                      // the program half, so the base half a lease prices off would come from the term-keyed slot -
                      // the sibling's, once this term is duplicated.
                      ...(!isProgramBackedColumn && {
                          [isDollarResidual ? 'baseResidualAmount' : 'baseResidualPercent']: residualOverride
                      })
                  };
        if (sellRate != null || ownResidualInfo) {
            actions.push(
                offerReduxActionCreators.setPaymentGridColumnOverride(offerType, index, {
                    ...(sellRate != null && { sellRate, rateType }),
                    ...(ownResidualInfo && { residualInfo: ownResidualInfo })
                })
            );
            restoredInputs[index] = {
                term,
                ...(sellRate != null && { sellRate: sellRateOverridden === true }),
                ...(ownResidualInfo && { residual: residualOverridden === true })
            };
        }

        const ownRows = paymentGridCells.reduce((byRow: Record<number, any>, row: any[], rowIndex: number) => {
            const cell = row?.[index];
            if (term != null && cell?.payment != null) {
                byRow[rowIndex] = {
                    ...(cell.po ?? {}),
                    monthlyPayment: cell.payment,
                    lenderCode: cell.lenderCode,
                    term,
                    downPayment: cashDown[rowIndex]
                };
            }
            return byRow;
        }, {});
        if (Object.keys(ownRows).length) {
            ownCellsByColumn[index] = ownRows;
        }
        if (residualOverride != null && !isTermSlotHeldBySelectedColumn) {
            actions.push(
                offerReduxActionCreators.updateProgramResidualOverride(
                    {
                        value: residualOverride,
                        months: term,
                        unitType: isDollarResidual ? RESIDUAL_DOLLAR : RESIDUAL_PERCENT
                    },
                    false,
                    {
                        isSelectedTerm: index === selectedTermIndex,
                        skipPaymentCall: true,
                        // Restore what a grid edit wrote, so the next payment call for this term reprices
                        // from the residual the deal was saved with rather than the program value.
                        syncBaseResidual: !isProgramBackedColumn
                    },
                    offerType
                )
            );
        }
    });

    if (isRedesignEnabled && Object.keys(restoredInputs).length) {
        actions.push(offerReduxActionCreators.setPaymentGridRestoredInputs(offerType, restoredInputs));
    }

    if (!isTemplateApplied && Object.keys(ownCellsByColumn).length) {
        actions.push(offerReduxActionCreators.setPaymentGridColumnCells(offerType, ownCellsByColumn));
    }

    // Enable PaymentGrid
    const downPaymentList = cashDown;
    const termMonthsList = terms;

    const preInitializedPaymentGrid = {
        downPaymentList,
        termMonthsList,
        // Undefined when the grid has no selected cell, which is legitimate for a re-priced grid. Left
        // unset rather than defaulted, so nothing downstream claims a current cell the dealer never chose.
        currentDownPayment: selectedDownPaymentIndex === undefined ? undefined : downPaymentList[selectedDownPaymentIndex],
        currentTermMonth: selectedTermIndex === undefined ? undefined : termMonthsList[selectedTermIndex],
        hasLenderPrograms,
        // Carry the MV-priced cells ([downPaymentIndex][termIndex] → { payment, ... }) so Sales View can
        // render the non-active grid cells from the baggage. On SV only the active term is hydrated into
        // terms.byId; the other cells were priced in MV solely for the grid and live only here. Templates
        // re-price against the new deal, so their (payment-less) cells are not forwarded.
        ...(!isTemplateApplied && { cells: paymentGridCells })
    };

    if (isTemplateApplied) {
        actions.push(offerReduxActionCreators.initializePaymentGridFromTemplate(preInitializedPaymentGrid, offerType));
    } else {
        actions.push(offerReduxActionCreators.updatePaymentGridToggle(true, preInitializedPaymentGrid));
    }

    return actions;
};

const dropCellsPricedForOtherCashDown = (grid: PaymentGrid, offerType: OfferType): PaymentGrid => {
    if (!Array.isArray(grid.cells)) return grid;
    return {
        ...grid,
        cells: grid.cells.map((row, rowIndex) =>
            Array.isArray(row)
                ? row.map((cell, columnIndex) => {
                      const pricedTermId = cell?.po?.id;
                      const term = grid.columns?.[columnIndex]?.term;
                      const dueAtSigning = grid.rows?.[rowIndex]?.dueAtSigning;
                      if (!pricedTermId || term == null || dueAtSigning == null) return cell;
                      if (pricedTermId === buildTermId(offerType, term, dueAtSigning)) return cell;
                      return {
                          payment: null,
                          lenderCode: null,
                          sellRate: null,
                          rateType: null,
                          residual: null,
                          isSelected: cell.isSelected,
                          po: null
                      };
                  })
                : row
        )
    };
};

/**
 * US2041815: restores a flex buy scenario's grid from the stored item, or nothing if there is none to use.
 *
 * Flex buy is the whole read path for now, deliberately: its grid arrives in one response and is replaced
 * wholesale, so putting it back is three dispatches and needs none of the per-cell reconciliation the
 * regular grid does (US2085313). Before this, a reloaded flex buy deal always re-priced, because
 * `INIT_SUCCESS` restores only the selected cell.
 *
 * A stale grid is not restored here. Manager View re-prices stale data — the DEP's rule — and leaving the
 * grid empty is what makes it do that, so a grid past both age conditions is simply not put back.
 */
/**
 * US2085313: restores a scenario's grid from the stored item, or nothing when there is none to use.
 *
 * The stored grid has the same rows/columns/cells shape as the `paymentGrid` the comparison entry used to
 * carry, so `buildPaymentGridActions` builds the axes, the per-column overrides and the toggle from it
 * unchanged. Only two things are new: the priced cells go into the terms node, and the timestamps come
 * back with them.
 *
 * Returning `[]` is how a stale grid re-prices. The existing build path prices whatever is not in state,
 * so declining to restore *is* the re-price. It is priced against the deal as reopened; nothing here
 * moves the deal. Note a stale entry *is* decompressed on the way: `resolvePaymentGridForScenario`
 * decompresses anything not `unusable`, and the axes are wanted from it so the grid has something to
 * price against.
 */
const buildRegularPaymentGridRestoreActions = (
    state: any,
    baggageScenario: Record<string, any>,
    offerType: OfferType,
    now: number,
    restoresColumnPrograms: boolean,
    dealTermMonths: number | undefined
): { grid: PaymentGrid; actions: any[] } | undefined => {
    const { grid: resolvedGrid, freshness } = resolvePaymentGridForScenario(
        {
            scenario: baggageScenario as any,
            payload: multiScenarioSelectors.getPaymentGridBaggagePayload(state),
            now
        },
        decompressPayload
    );

    /**
     * No grid at all is the only case that gives up: an `unusable` entry, or none stored.
     *
     * A grid that decompressed still returns, with no restore actions when it is stale. Returning
     * `undefined` here used to lose the axes as well as the cells, because dash-bff drops the comparison
     * entry's inline `paymentGrid` once the compressed item is written — so there was nothing left for
     * `buildPaymentGridActions` to build from and the grid vanished instead of re-pricing. Handing the
     * decompressed grid back keeps the axes, and the empty action list is what makes the rows price.
     */
    if (!resolvedGrid) return undefined;
    const grid = featureToggleSelectors.isGridCellCashDownMatchEnabled(state)
        ? dropCellsPricedForOtherCashDown(resolvedGrid, offerType)
        : resolvedGrid;
    if (freshness !== 'fresh') return { grid, actions: [] };

    const byId: Record<string, any> = {};
    const cellPricedAt: Record<string, number> = {};
    const quotedTermsByRow: Record<number, number[]> = {};
    const programRecords: Record<string, any>[] = [];

    // Axes are read defensively for the same reason the cells are: the bytes come off a deal saved by an
    // older build, and a malformed axis would otherwise take the whole scenario ingest down.
    (grid.rows ?? []).forEach((row, rowIndex) => {
        (grid.columns ?? []).forEach((column, columnIndex) => {
            if (!row || !column) return;
            const cell = grid.cells?.[rowIndex]?.[columnIndex];
            // A cell whose payment call had failed carries no quote, so it stays empty and prices on demand.
            if (!cell?.po) return;

            // The producer's own helper: restore and save must key cells identically, or a restored cell
            // is invisible to the next save and the grid silently loses it.
            const termId = buildTermId(offerType, column.term, row.dueAtSigning);
            if (!termId) return;

            byId[termId] = cell.po;
            if (restoresColumnPrograms && column.term !== dealTermMonths) programRecords.push(cell.po);
            if (cell.at !== undefined) cellPricedAt[termId] = cell.at;
            quotedTermsByRow[rowIndex] = [...(quotedTermsByRow[rowIndex] ?? []), column.term];
        });
    });

    // Fresh, but no cell carried a quote — the axes still come back so the grid prices against them.
    if (!Object.keys(byId).length) return { grid, actions: [] };

    /**
     * The columns' verdicts, put back with the cells they describe.
     *
     * A masked Lease cell paints from its column's source, not from its term record, and only a payment
     * outcome writes one — so without this a restored Lease grid came up blank with a payment in every
     * cell. Absent on grids stored before the source was saved, which simply price as they did before.
     */
    // `grid.columns` is safe here: reaching this line means a cell was keyed off it, so it is a populated
    // array. The axes are read defensively above, where that is not yet true.
    const columnSources = grid.columns.reduce<Record<number, string>>((sources, column) => {
        if (column?.source) sources[column.term] = column.source;
        return sources;
    }, {});
    const decidedColumns = grid.columns.reduce<Record<number, string>>((sources, column, columnIndex) => {
        if (column?.source) sources[columnIndex] = column.source;
        return sources;
    }, {});

    return {
        grid,
        actions: [
            offerReduxActionCreators.restorePaymentGridTerms(offerType, byId),
            ...(programRecords.length ? [offerReduxActionCreators.restorePaymentGridPrograms(offerType, programRecords)] : []),
            ...(Object.keys(columnSources).length
                ? [offerReduxActionCreators.setPaymentGridColumnSources(offerType, columnSources, { replace: true })]
                : []),
            ...(Object.keys(decidedColumns).length
                ? [offerReduxActionCreators.setPaymentGridDecidedColumns(offerType, decidedColumns)]
                : []),
            ...(Object.keys(cellPricedAt).length
                ? [offerReduxActionCreators.setPaymentGridCellPricedAt(offerType, cellPricedAt)]
                : []),
            offerReduxActionCreators.restorePaymentGridRowQuotes(offerType, quotedTermsByRow)
        ]
    };
};

const buildFlexBuyPaymentGridRestoreActions = (state: any, scenarioVersionId: string | undefined, now: number): any[] => {
    // No toggle check here: the one caller tests it first, so a second one would be unreachable.
    const payload = multiScenarioSelectors.getPaymentGridBaggagePayload(state);
    const entry = getPaymentGridScenarioEntry(payload, scenarioVersionId);
    // The caller's clock, so the flex buy and regular restores judge freshness the same way rather than one
    // taking `getPaymentGridFreshness`'s default.
    if (!entry || getPaymentGridFreshness(entry.oldestAt, now) !== 'fresh') return [];

    const grid = decompressPayload<PaymentGrid>({ codec: 'gzip', enc: 'base64', data: entry.data });
    const restored = restoreFlexBuyGridFromStored(grid);
    if (!restored) return [];

    const { flexBuyPaymentGridData, flexBuyTerms, flexBuyPercents, flexBuyDownPayments } = restored;

    return [
        // Axes first: the grid data is indexed against them, so putting the cells in before the axes they
        // belong to would render one grid's rows under another's headings.
        offerReduxActionCreators.updateFlexBuyPaymentGridAxisValues({ flexBuyTerms, flexBuyPercents, flexBuyDownPayments }),
        offerReduxActionCreators.updateFlexBuyPaymentGridData(flexBuyPaymentGridData),
        offerReduxActionCreators.setFlexBuyPaymentGridPricedAt(entry.oldestAt as number),
        // Last, because the toggle is what opens the grid, and it should open onto data that is already there.
        offerReduxActionCreators.updateFlexBuyPaymentGridToggle(true)
    ];
};

/**
 * Thunk action creator that dispatches actions for loading PaymentGrid/NineBox for individual scenarios
 */
export const handleLoadScenarioPaymentGrid =
    (scopeId: string, baggageScenario: Record<string, any>) => (dispatch: any, getState: any) => {
        const state = getState();
        const isTemplateApplied = dealTemplateSelectors.getIsDealTemplateApplied(state);
        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId);
        const offerType = scopeState && scopeState.offer.offerType;

        /**
         * A flex buy deal takes the flex buy restore instead of the regular one, not as well as it: the two
         * write different slices, and a flex buy deal has no `paymentGrid` on its comparison entry to build
         * the regular actions from. Templates re-price against the new deal, so they keep re-pricing.
         */
        /**
         * The toggle is tested first, so with it off this whole branch costs one boolean read and nothing
         * else is evaluated — no deal-type check, no baggage lookup, no decompression.
         */
        /**
         * Which restore a deal takes is decided by the deal, not by whether the other one produced
         * anything. A flex buy deal whose stored grid is not a flex buy grid must re-price — falling
         * through to the regular restore would push flex buy cells into the regular grid's terms node.
         *
         * Templates re-price against the new deal, so they restore nothing either way.
         */
        const isRestoreEnabled = featureToggleSelectors.isPaymentGridBaggageCompressionEnabled(state) && !isTemplateApplied;
        // Behind `isRestoreEnabled`, so the claim above stays true: with the toggle off this costs one
        // boolean read and no deal-type lookup runs.
        const isFlexBuyDeal = Boolean(isRestoreEnabled && scopeState && offerReduxSelectors.isFlexBuyPayment(scopeState));
        /**
         * One clock per scenario, not per load — deliberately, and narrower than what
         * `resolvePaymentGridForScenario` documents.
         *
         * This thunk is dispatched once per scenario: from the loops in `addScenariosToState` and
         * `addTemplateScenariosToState`, and once per `SCENARIO_HYDRATE_RECEIVED` frame. Hoisting the clock
         * into those loops would buy nothing, because their reads land in the same tick; the async path is
         * the only one with a real window, and there each scenario arrives in its own action, so there is no
         * shared moment to anchor them to. What this does guarantee is that the freshness verdict and the
         * cells restored under it come from one reading, which is what a split clock would break.
         */
        const now = Date.now();

        const flexBuyActions = isFlexBuyDeal ? buildFlexBuyPaymentGridRestoreActions(state, baggageScenario?.id, now) : [];
        const isRedesignActive = offerReduxSelectors.isAspPaymentGridRedesignActive(state, offerType);
        const isExplicitLenderGridActive = offerReduxSelectors.isExplicitLenderProgramGridActive(state, offerType);
        const restoresColumnPrograms = isRedesignActive && !(offerType === LEASE && isExplicitLenderGridActive);

        /**
         * The stored grid is preferred over the comparison entry's inline copy, and only one of the two
         * ever exists: a scenario saved with the toggle on has no inline grid, one saved before it has no
         * stored item. Either way `buildPaymentGridActions` runs exactly once, over whichever grid won —
         * the shapes are identical, so nothing downstream can tell them apart.
         *
         * Nothing restored means the grid prices, which is how a stale grid re-prices: against the deal as
         * reopened, without the deal being moved.
         */
        const restored =
            isRestoreEnabled && !isFlexBuyDeal
                ? buildRegularPaymentGridRestoreActions(
                      state,
                      baggageScenario,
                      offerType,
                      now,
                      restoresColumnPrograms,
                      scopeState ? offerReduxSelectors.getSelectedTermMonths(scopeState, offerType) : undefined
                  )
                : undefined;

        const scenarioForGrid = restored ? { ...baggageScenario, paymentGrid: restored.grid } : baggageScenario;

        const actions = flexBuyActions.length
            ? flexBuyActions
            : [
                  ...(restored?.actions ?? []),
                  ...buildPaymentGridActions(
                      scenarioForGrid,
                      offerType,
                      isTemplateApplied,
                      isRedesignActive,
                      isExplicitLenderGridActive
                  )
              ];
        for (const action of actions) {
            dispatch(scopedActions.createScopedAction(scopeId, action));
        }
    };

/** Typed so an `offer` rename breaks the build instead of silently seeding nothing; the selectors downstream all take `any`. */
type StartingCashScopeState = Pick<StateTree, 'offer'>;

/**
 * Baggage value, else a derived fallback for older deals — deductDueAtSigning-only, the one case where the
 * derived amount IS the saved input; elsewhere it would overwrite the user's number.
 */
export const resolveStartingCash = (
    scopeState: StartingCashScopeState | undefined,
    baggageScenario?: ScenarioReference,
    isDueAtSigningAmountDeduct = false
): number | undefined => {
    // No offer slice means the scope has not hydrated yet, so there is nothing to seed into.
    if (!scopeState?.offer) {
        return undefined;
    }

    // A saved 0 is a real value, so check for a number rather than truthiness. Returned on Finance scopes too:
    // the seed only writes the lease bucket, but skipping it drops the persisted value on the next save.
    const baggageStartingCash = baggageScenario?.startingCash;
    if (typeof baggageStartingCash === 'number') {
        return baggageStartingCash;
    }

    // No baggage value: the derived fallback below reads lease terms off this scope, so it is lease-only.
    // `offer` is non-nullish past the guard above, so no optional chain here — it would be a dead branch.
    if (scopeState.offer.offerType !== LEASE || !isDueAtSigningAmountDeduct) {
        return undefined;
    }

    // One-pay comes off the payload frequency, not the term: the `isOnePayLease` selector reads only the term
    // and defaults to Monthly when the term carries none.
    const isOnePayLease = paymentPayloadUtils.getPayloadPaymentFrequency(scopeState, LEASE) === PaymentScheduleTypes.OnePayLease;

    // Term read directly, not through the `|| 0` selectors: those can't tell an unresolvable term from a real
    // 0, and seeding 0 would show $0 over the saved amount and persist it on the next save. An unresolvable
    // term is `{}`, never nullish, so the typeof check below is what rejects it.
    const selectedTerm = offerReduxSelectors.getSelectedTerm(scopeState, LEASE);

    // Same split as the slideout's Cash Down field (getCashDownDisplayValue) and the lease payment grid: a
    // "Cash" method makes cash down the derived netCashDown, any other method leaves it as the customer's own
    // cashDueFromCustomer. Cash Received is that same amount, so it has to resolve the same way.
    const equityAllocationMethod = offerReduxSelectors.getEquityAllocationMethod(scopeState);
    const hasCash = !!equityAllocationMethod && offerReduxUtils.isCashEquityAllocationMethod(equityAllocationMethod);
    const derivedStartingCash = !isOnePayLease && hasCash ? selectedTerm.cashDueFromCustomer : selectedTerm.netCashDown;

    return typeof derivedStartingCash === 'number' ? derivedStartingCash : undefined;
};

/**
 * Seeds one scope's "Cash Received" from its comparison baggage. Dispatch at the end of that scope's own
 * hydration — under `enableMVMSLoadAsync` a card goes interactive long before ingest completes.
 */
export const handleLoadScenarioStartingCash =
    (scopeId: string, baggageScenario?: ScenarioReference) =>
    (dispatch: any, getState: any): void => {
        const state = getState();
        if (!featureToggleSelectors.isEquityAllocationOrderEnabled(state)) {
            return;
        }

        const isPrimaryScope = scopeId === scopedStateSelectors.getPrimaryScopeId(state);
        const isTemplateScope =
            dealTemplateSelectors.getIsDealTemplateApplied(state) || dealTemplateSelectors.isDealTemplateManuallyApplied(state);
        if (!isPrimaryScope && isTemplateScope) {
            return;
        }

        // Dealer-level setting, so it comes off the root state rather than the scope.
        const isDueAtSigningAmountDeduct = dealerSelectors.getIsDueAtSigninAmountDeduct(state);
        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId);
        const startingCash = resolveStartingCash(scopeState, baggageScenario, isDueAtSigningAmountDeduct);
        if (startingCash === undefined) {
            return;
        }

        const seedAction = offerReduxActionCreators.updateDownPaymentImmediate(startingCash, LEASE);

        // Seeds land after the unsaved-changes baseline, so they must re-baseline or the deal opens dirty — but
        // not once it is already dirty: the snapshot is global, so a late seed would absorb another scope's edit.
        const rebaselineUnsavedChanges = !unsavedChangesSelectors.currentOfferHasUnsavedChanges(state);

        dispatch(
            scopedActions.createScopedAction(scopeId, {
                ...seedAction,
                meta: { ...seedAction.meta, rebaselineUnsavedChanges, userIntent: offerReduxConstants.DEAL_HYDRATION_INTENT }
            })
        );
    };

/**
 * Thunk action creator that dispatches actions for fetching additional scenarios for "make current"
 */
export const handleFetchMakeCurrentMultipleScenarios = () => (dispatch: any, getState: any) => {
    const state = getState();

    const dealXgVersion = offerReduxSelectors.getDealXgVersion(state);
    const compObj = multiScenarioSelectors.getComparisonByDealVersion(dealXgVersion, state);

    if (compObj && compObj.scenarios?.length > 1) {
        const dealXgId = offerReduxSelectors.getDealXgId(state);
        const nonPrimaryScenarioIds = compObj.scenarios.filter(({ isPrimary }) => !isPrimary).map(({ id }) => id);

        dispatch(fetchScenarios(dealXgId, nonPrimaryScenarioIds));
    } else {
        dispatch({ type: MAKE_CURRENT_COMPLETED });
        dispatch(addMakeCurrentAlternateScenariosToState([]));
    }

    // "Make current" bypasses handleFetchMultipleScenarios, so the primary seeds here; alternates seed
    // themselves in addMakeCurrentAlternateScenariosToState.
    dispatch(
        handleLoadScenarioStartingCash(
            scopedStateSelectors.getPrimaryScopeId(state),
            compObj?.scenarios?.find(({ isPrimary }) => isPrimary)
        )
    );
};

/**
 * Add all alternate scenarios retrieved from DXG to the scope state for "make current", then initializes the scopes.
 */
export const addMakeCurrentAlternateScenariosToState =
    (nonPrimaryScenarios: any[]) =>
    (dispatch: any, getState: any): void => {
        if (nonPrimaryScenarios) {
            const state = getState();
            const dealXgVersion = offerReduxSelectors.getDealXgVersion(state);
            const compObj = multiScenarioSelectors.getComparisonByDealVersion(dealXgVersion, state);
            const baggageScenariosById = new Map((compObj?.scenarios ?? []).map((scenario) => [scenario.id, scenario]));

            const nonPrimaryScenarioIds = nonPrimaryScenarios.map(({ offer }: any) => offer?.dealExchangeVersionId);

            // Add each non-primary scenario to state
            for (const scenario of nonPrimaryScenarios) {
                const scenarioId = scenario.offer?.dealExchangeVersionId;
                const newAction = scopedActions.createScopedAction(scenarioId, {
                    type: initActionTypes.INIT_PENCIL_SUCCESS,
                    payload: withPrimaryOfferToggles(withoutScenarioFeatureToggles(scenario), getState())
                });
                dispatch(newAction);
            }

            dispatch(scopedActionsCreators.initializeScopes(nonPrimaryScenarioIds));

            // Cash Received last, closing this path's hydration sequence as in addAlternateScenariosToState
            // (US2067616). "Make current" has no ingest-complete boundary to hang it off.
            for (const scenarioId of nonPrimaryScenarioIds) {
                dispatch(handleLoadScenarioStartingCash(scenarioId, baggageScenariosById.get(scenarioId)));
            }
        }
    };

export const outOfBandBannerDisplayed = () => ({
    type: OUT_OF_BAND_BANNER_DISPLAYED
});

export const outOfBandBannerHidden = () => ({
    type: OUT_OF_BAND_BANNER_HIDDEN
});

export const copyDealScenarioClicked = () => ({
    type: COPY_DEAL_SCENARIO_CLICKED
});

export const deleteDealScenarioClicked = () => ({
    type: DELETE_DEAL_SCENARIO_CLICKED
});

export const selectPrimaryDealScenarioClicked = () => ({
    type: SELECT_PRIMARY_DEAL_SCENARIO_CLICKED
});

export const addUninitializedScenariosToState =
    () =>
    (dispatch: any, getState: any): void => {
        const state = getState();
        const dealXgVersion = offerReduxSelectors.getDealXgVersion(state);
        const comparison = offerInfoSelectors.getIsMakingCurrent(state)
            ? multiScenarioSelectors.getComparisonByDealVersion(dealXgVersion, state)
            : multiScenarioSelectors.getActiveComparison(state);

        if (comparison) {
            const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
            const isDealTemplate = dealTemplateSelectors.getIsDealTemplateApplied(state);
            const nonPrimaryScenarioIds = comparison.scenarios.filter(({ isPrimary }) => !isPrimary).map(({ id }) => id);

            const order = isDealTemplate
                ? getOrderForTemplateScenarios(comparison, primaryScopeId)
                : getOrderFromRetrievedScenarios(comparison, nonPrimaryScenarioIds, primaryScopeId);

            // Must have the scope state partially populated BEFORE fully hydrating with the INIT_PENCIL_SUCCESS dispatch
            dispatch(scopedActionsCreators.createUninitializedScopesFromOrder(order));
        }
    };

export const removeAlternateScenariosFromState =
    () =>
    (dispatch: any): void => {
        dispatch(scopedActionsCreators.deleteNonPrimaryScopes());
    };

export const updateActiveComparison = (payload: ScenarioReference) => ({
    type: UPDATE_ACTIVE_COMPARISON,
    payload
});
